import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useTriggerSyncActionMutation } from "state/rtkQuery/sync/syncQueries";
import sortByLabel from "utils/sortByLabel";

import { buildParams, describeDates, describeScope, emptyValues, getAction, needsConfirmation, validateValues } from "./syncActions";
import { normalizeApiError } from "./syncFormat";
import { ScopeChip } from "./SyncParts";
import { useSyncTasks } from "./SyncTasksContext";

// API field names the backend may point at, mapped to this form's keys.
const FIELD_ALIAS = { org_id: "org" };

const RunActionDrawer = ({ actionId, onClose, onQueued }) => {
  const action = actionId ? getAction(actionId) : null;
  const { trackTask } = useSyncTasks();
  const { data: orgs } = useGetActiveOrganizationsQuery(undefined, { skip: !action });
  const { data: platform } = useGetPlatformsQuery(undefined, { skip: !action });
  const selectedPlatformId = useSelector((state) => state.ecrOpsPlatform?.selectedPlatformId);
  const platformId = selectedPlatformId || platform?._id;
  const [trigger, { isLoading }] = useTriggerSyncActionMutation();

  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (action) {
      setValues(emptyValues(action));
      setErrors({});
      setSubmitError(null);
      setConfirming(false);
    }
  }, [actionId]); // eslint-disable-line

  const orgOptions = useMemo(() => sortByLabel(orgs, "name"), [orgs]);
  const orgName = orgOptions.find((org) => org._id === values.org)?.name;
  if (!action) return null;

  const set = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const review = () => {
    const found = validateValues(action, values);
    setErrors(found);
    setSubmitError(null);
    if (Object.keys(found).length) return;
    if (needsConfirmation(action, values)) setConfirming(true);
    else submit();
  };

  const submit = async () => {
    setConfirming(false);
    const params = buildParams(action, values);
    try {
      const body = await trigger({ path: action.path, params }).unwrap();
      trackTask({
        id: body.task_id,
        actionId: action.id,
        title: action.title,
        taskName: body.task || action.task,
        queue: body.queue || params.queue || action.queue,
        scope: [describeScope(action, values, orgName), describeDates(action, values)].filter(Boolean).join(" · "),
        owner: body.owner,
        triggeredBy: body.triggered_by,
        backendScope: body.scope,
      });
      onQueued?.({ title: action.title, taskId: body.task_id });
      onClose();
    } catch (error) {
      const normalized = normalizeApiError(error);
      const mapped = {};
      Object.entries(normalized.fieldErrors).forEach(([name, text]) => {
        mapped[FIELD_ALIAS[name] || name] = text;
      });
      setErrors(mapped);
      setSubmitError(normalized);
    }
  };

  const field = (key, extra = {}) => ({
    value: values[key] ?? "",
    onChange: (event) => set(key, event.target.value),
    error: Boolean(errors[key]),
    helperText: errors[key] || extra.help,
    size: "small",
    fullWidth: true,
    sx: { mb: 2 },
  });

  const dedupOn = Boolean(values.bypass_dedup);
  const forceOn = Boolean(values.force);

  return (
    <>
      <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}>
        <Box sx={{ p: "18px 22px", borderBottom: "1px solid #e2e8f0" }}>
          <Typography sx={{ color: "#6a9a1e", fontSize: 11.5, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" }}>Run action</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700, mt: 0.25 }}>{action.title}</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 12.8, mt: 0.75 }}>{action.description}</Typography>
          <Box sx={{ mt: 1 }}><ScopeChip scope={action.scope} /></Box>
        </Box>

        <Box sx={{ p: "20px 22px", overflow: "auto", flex: 1 }}>
          {!action.fields.length && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This action has no inputs. It applies to <b>everything</b> in scope, so you will be asked to confirm.
            </Alert>
          )}
          {submitError && (
            <Alert severity={submitError.uncertain ? "warning" : "error"} sx={{ mb: 2 }} data-testid="run-error">
              {submitError.message}
            </Alert>
          )}

          {action.fields.map((fieldDef) => {
            if (fieldDef.kind === "org") {
              return (
                <Autocomplete
                  key="org"
                  size="small"
                  options={orgOptions}
                  getOptionLabel={(option) => option?.name ?? ""}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  value={orgOptions.find((org) => org._id === values.org) ?? null}
                  onChange={(_, option) => set("org", option?._id ?? "")}
                  noOptionsText="No matching organization"
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={fieldDef.required ? "Organization *" : "Organization (optional)"}
                      placeholder={fieldDef.required ? "Select an organization…" : "All active organizations"}
                      error={Boolean(errors.org)}
                      helperText={errors.org || (fieldDef.required ? undefined : "Leave empty to run for every active organization.")}
                    />
                  )}
                />
              );
            }
            if (fieldDef.kind === "text") {
              return <TextField key={fieldDef.param} label={`${fieldDef.label}${fieldDef.required ? " *" : ""}`} {...field(fieldDef.param, { help: fieldDef.help })} />;
            }
            if (fieldDef.kind === "number") {
              return (
                <TextField
                  key={fieldDef.param}
                  type="number"
                  label={fieldDef.label}
                  inputProps={{ min: fieldDef.min, max: fieldDef.max }}
                  {...field(fieldDef.param, { help: `Allowed: ${fieldDef.min.toLocaleString("en-US")} to ${fieldDef.max.toLocaleString("en-US")} (default ${fieldDef.def.toLocaleString("en-US")})` })}
                />
              );
            }
            if (fieldDef.kind === "date") {
              return <TextField key={fieldDef.param} type="date" label={fieldDef.label} InputLabelProps={{ shrink: true }} {...field(fieldDef.param, { help: fieldDef.help })} />;
            }
            if (fieldDef.kind === "dates") {
              return (
                <Box key="dates" sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                  <TextField type="date" label="Start date" InputLabelProps={{ shrink: true }} {...field("start_date", { help: `Empty = ${fieldDef.whenEmpty}` })} />
                  <TextField type="date" label="End date" InputLabelProps={{ shrink: true }} {...field("end_date", { help: "Inclusive. Empty = the start date." })} />
                </Box>
              );
            }
            if (fieldDef.kind === "platform") {
              return (
                <TextField key="platform" select label="Platform (optional)" {...field("platform_id", { help: "Empty = every enabled platform." })}>
                  <MenuItem value="">All enabled platforms</MenuItem>
                  {platformId && <MenuItem value={platformId}>{platform?.name || "Current platform"}</MenuItem>}
                </TextField>
              );
            }
            return null;
          })}

          <Box sx={{ border: "1px dashed #cbd5e1", borderRadius: "10px", p: "4px 14px 14px", background: "#fafbfc" }}>
            <Typography sx={{ fontWeight: 600, fontSize: 12.5, color: "#64748b", pt: 1.25, pb: 1 }}>Advanced</Typography>
            {action.fields.some((f) => f.kind === "bypass") && (
              <FormControlLabel
                control={<Checkbox size="small" checked={dedupOn} onChange={(event) => set("bypass_dedup", event.target.checked)} />}
                label={<span><b>Run even if it ran recently</b><br /><span style={{ color: "#64748b", fontSize: 12 }}>Skips the "already done" guard. Use only when you need a repeat run.</span></span>}
                sx={{ alignItems: "flex-start", mb: 1 }}
              />
            )}
            {action.fields.some((f) => f.kind === "force") && (
              <FormControlLabel
                control={<Checkbox size="small" checked={forceOn} onChange={(event) => set("force", event.target.checked)} />}
                label={<span><b>Force update</b><br /><span style={{ color: "#64748b", fontSize: 12 }}>Ignore the "updated recently" skip for this user.</span></span>}
                sx={{ alignItems: "flex-start", mb: 1 }}
              />
            )}
            <TextField label="Queue" placeholder={`${action.queue} (default)`} {...field("queue", { help: "Leave empty. Only change this if you know a worker listens on that queue." })} sx={{ mt: 1, mb: 0 }} />
          </Box>
        </Box>

        <Box sx={{ p: "14px 22px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 1.5, background: "#fafbfc" }}>
          <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button variant="contained" onClick={review} disabled={isLoading} sx={{ background: "#86BC25", textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { background: "#6a9a1e", boxShadow: "none" } }}>
            {isLoading ? "Queuing…" : "Review & run"}
          </Button>
        </Box>
      </Drawer>

      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Run “{action.title}”?</DialogTitle>
        <DialogContent>
          <Box sx={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", p: "10px 14px", fontSize: 13 }}>
            <Row label="Scope" value={describeScope(action, values, orgName)} />
            {describeDates(action, values) && <Row label="Dates" value={describeDates(action, values)} />}
            <Row label="Queue" value={values.queue?.trim() || action.queue} />
          </Box>
          {action.danger && <Alert severity="warning" sx={{ mt: 1.5 }}>This asks GitHub to remove inactive users across every organization. It can't be cancelled from here.</Alert>}
          {dedupOn && <Alert severity="warning" sx={{ mt: 1.5 }}>"Run even if it ran recently" is on: the usual duplicate guard is skipped.</Alert>}
          {forceOn && <Alert severity="warning" sx={{ mt: 1.5 }}>Force is on: the recent-update skip is ignored for this user.</Alert>}
          <Typography sx={{ color: "#64748b", fontSize: 12.5, mt: 1.5 }}>Accepted means queued, not finished. You can follow it in the Task tracker.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: "8px 24px 18px" }}>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button variant="contained" color={action.danger ? "error" : "primary"} onClick={submit} disabled={isLoading} sx={{ textTransform: "none", fontWeight: 700 }}>
            {action.danger ? "Yes, run it" : "Run"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const Row = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.4, gap: 2 }}>
    <span style={{ color: "#64748b" }}>{label}</span>
    <b style={{ fontWeight: 600, textAlign: "right" }}>{value}</b>
  </Box>
);

export default RunActionDrawer;
